/**
 * Shared date utility functions for the application
 */

export const MS_PER_MINUTE = 60_000;
export const MS_PER_DAY = 86_400_000;
export const FRENCH_MONTH_NAMES = ['jan.', 'fév.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/**
 * Parse a YYYY-MM-DD date string as a local date (not UTC).
 * This avoids timezone issues where "2025-01-19" becomes "2025-01-18" in negative UTC offsets.
 * @param dateString - Date string in YYYY-MM-DD or ISO DateTime format (YYYY-MM-DDTHH:mm:ss)
 * @returns Date object set to local midnight
 */
export function parseDateStringAsLocal(dateString: string): Date {
    // Extract just the date part if it's an ISO DateTime string
    const dateOnly = dateString.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Normalize the deliberately loose value emitted by Vuetify's date picker.
 * Depending on the configured date adapter, a single selection can be a Date,
 * an ISO date string, or a one-item array containing either representation.
 */
export function normalizeDatePickerValue(value: unknown): Date | null {
    const candidate = Array.isArray(value) ? value[0] : value;
    let date: Date;

    if (candidate instanceof Date) {
        date = new Date(candidate.getTime());
    } else if (typeof candidate === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(candidate)) {
        date = parseDateStringAsLocal(candidate);
    } else {
        return null;
    }

    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Check if a given day is a weekend (Saturday or Sunday)
 * @param day - Day of the month (1-31)
 * @param yearMonth - Month in YYYY-MM format
 * @returns True if the day is a weekend
 */
export function isWeekend(day: number, yearMonth: string): boolean {
    const [year, month] = yearMonth.split('-').map(Number);
    return isWeekendDate(new Date(year, month - 1, day));
}

/**
 * Format a date to YYYY-MM-DD format
 * @param year - Year
 * @param month - Month (1-12)
 * @param day - Day of month
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateString_YYYYMMDD(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Format a date to YYYY-MM-DDTHH:mm:ss format
 * @param year - Year
 * @param month - Month (1-12)
 * @param day - Day of month
 * @returns Date string in ISO format
 */
export function formatDateString_ISO(year: number, month: number, day: number): string {
    return `${formatDateString_YYYYMMDD(year, month, day)}T00:00:00`;
}

/**
 * Parse a year-month string into year and month numbers
 * @param yearMonth - Month in YYYY-MM format
 * @returns Tuple of [year, month]
 */
export function parseYearMonth(yearMonth: string): [number, number] {
    return yearMonth.split('-').map(Number) as [number, number];
}

/**
 * Get the first day (Sunday) of the current week
 * @returns Date object for the Sunday of the current week
 */
export function getStartOfWeek(): Date {
    const today = new Date();
    const day = today.getDay();
    const diff = -day; // Days to subtract to get to Sunday
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + diff);
    return sunday;
}

/**
 * Get the last day (Saturday) of the current week
 * @returns Date object for the Saturday of the current week
 */
export function getEndOfWeek(): Date {
    const today = new Date();
    const day = today.getDay();
    const diff = 6 - day; // Days to add to get to Saturday
    const saturday = new Date(today);
    saturday.setDate(today.getDate() + diff);
    return saturday;
}

/**
 * Format a Date object to YYYY-MM-DD format for input fields
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Is this string exactly one real calendar day, written YYYY-MM-DD?
 *
 * The round-trip is what makes it strict (#998). `parseDateStringAsLocal` builds a Date from the
 * three numbers, and `new Date(2026, 1, 29)` rolls over to 1 March rather than failing — so a
 * NaN check alone accepts 2026-02-29 and the screen then shows a different day than the one it
 * was asked for. Re-formatting and comparing catches every such rollover.
 */
export function isValidDateString(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = parseDateStringAsLocal(value);
    return !Number.isNaN(date.getTime()) && formatDateForInput(date) === value;
}

/**
 * Get the current date in YYYY-MM-DD format
 */
export function getCurrentDate(): string {
    return formatDateForInput(new Date());
}

/**
 * Get Monday of the week containing the given date
 * @param date - Date object or date string in YYYY-MM-DD format
 * @returns Date object for Monday of that week
 */
export function getMondayOfWeek(date: Date | string): Date {
    const d = typeof date === 'string' ? parseDateStringAsLocal(date) : new Date(date.getTime());
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    d.setDate(diff);
    return d;
}

/**
 * Get Sunday of the week containing the given date
 * @param date - Date object or date string in YYYY-MM-DD format
 * @returns Date object for Sunday of that week
 */
export function getSundayOfWeek(date: Date | string): Date {
    const d = typeof date === 'string' ? parseDateStringAsLocal(date) : new Date(date.getTime());
    const day = d.getDay();
    const diff = d.getDate() - day; // Get to Sunday
    d.setDate(diff);
    return d;
}

/**
 * Calculate the date range for the teacher assignment grid based on a base date.
 * The range spans from the beginning of the week prior to the base date's week,
 * to the end of the 2nd week after the base date's week.
 * Weeks begin on Sunday and end on Saturday.
 * 
 * For example, if base date is January 13th 2016 (Wednesday):
 * - Base date week: Sun Jan 10 - Sat Jan 16
 * - Prior week: Sun Jan 3 - Sat Jan 9
 * - Week after: Sun Jan 17 - Sat Jan 23
 * - 2nd week after: Sun Jan 24 - Sat Jan 30
 * - Result: Jan 3 - Jan 30 (4 weeks total)
 * 
 * @param baseDate - The reference date (defaults to today)
 * @returns Object with startDate and endDate (both Date objects)
 */
export function getTeacherAssignmentDateRange(baseDate: Date | string = new Date()): { startDate: Date; endDate: Date } {
    const base = typeof baseDate === 'string' ? parseDateStringAsLocal(baseDate) : new Date(baseDate);

    // Get Sunday of the base date's week (weeks start on Sunday)
    const baseSun = getSundayOfWeek(base);

    // Start date: Sunday of the week before the base week
    const startDate = new Date(baseSun);
    startDate.setDate(baseSun.getDate() - 7); // Go back one week

    // End date: Saturday of the 2nd week after the base week
    // From baseSun to Saturday: +6 days (to end of base week)
    // Then +14 days (2 more weeks)
    // Total: +20 days from baseSun
    const endDate = new Date(baseSun);
    endDate.setDate(baseSun.getDate() + 20);

    return { startDate, endDate };
}

/**
 * Add days to a date
 * @param date - Date object or date string
 * @param days - Number of days to add (can be negative)
 * @returns New Date object
 */
export function addDays(date: Date | string, days: number): Date {
    const d = typeof date === 'string' ? parseDateStringAsLocal(date) : new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

/**
 * Add weeks to a date
 * @param date - Date object or date string
 * @param weeks - Number of weeks to add (can be negative)
 * @returns New Date object
 */
export function addWeeks(date: Date | string, weeks: number): Date {
    return addDays(date, weeks * 7);
}

/**
 * Get all days in a date range (inclusive)
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Array of Date objects for each day in range
 */
export function getDaysInRange(startDate: Date | string, endDate: Date | string): Date[] {
    const start = typeof startDate === 'string' ? parseDateStringAsLocal(startDate) : new Date(startDate);
    const end = typeof endDate === 'string' ? parseDateStringAsLocal(endDate) : new Date(endDate);

    const days: Date[] = [];
    const current = new Date(start);

    while (current <= end) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }

    return days;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * @param date - Date object or date string
 * @returns True if the date is a weekend
 */
export function isWeekendDate(date: Date | string): boolean {
    const d = typeof date === 'string' ? parseDateStringAsLocal(date) : date;
    const dayOfWeek = d.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
}

/**
 * Whether a calendar Saturday/Sunday should render as a NON-WORKING weekend column in a
 * time-axis matrix (teacher-assignment-grid.vue, group-attendance-grid.vue), given the
 * school's own schedule.
 *
 * This is deliberately NOT the same question as `isWeekend`/`isWeekendDate` above: those
 * answer "is this literally a Saturday or Sunday", with no notion of a schedule. A school
 * whose SchedulePatternPeriods enable Saturday and/or Sunday classes treats those days as
 * ordinary working days, so the grid must not grey them out or block clicks on them —
 * `worksWeekends` carries that fact in from the caller
 * (`periods.some(p => p.saturday || p.sunday)`). Do not merge this with
 * `isWeekend`/`isWeekendDate`: callers that need the pure calendar answer (holiday
 * expansion, generic date pickers) must keep using those (#748).
 *
 * @param date - Date object
 * @param worksWeekends - True when the school's configured periods include Saturday or Sunday
 * @returns True when the date is a Saturday/Sunday AND the school does not work weekends
 */
export function isNonWorkingWeekend(date: Date, worksWeekends: boolean): boolean {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6 || dayOfWeek === 0) return !worksWeekends;
    return false;
}

/**
 * Get the abbreviated French weekday name for a date (e.g. "lun", "sam").
 * @param date - Date object
 * @returns Three-letter lowercase French weekday abbreviation
 */
export function getWeekdayName(date: Date): string {
    const weekdays = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
    return weekdays[date.getDay()];
}

/**
 * Get the day-of-month number for a date (1-31).
 * @param date - Date object
 */
export function getDayOfMonth(date: Date): number {
    return date.getDate();
}

/**
 * Get the abbreviated French month name for a date, from FRENCH_MONTH_NAMES.
 * @param date - Date object
 */
export function getMonthName(date: Date): string {
    return FRENCH_MONTH_NAMES[date.getMonth()];
}

/**
 * Whether a date is a week-boundary column (Sunday) in a Sunday-first calendar grid.
 * Used by the time-axis matrices to draw a week-separator border.
 * @param date - Date object
 */
export function isWeekBoundary(date: Date): boolean {
    return date.getDay() === 0;
}

/**
 * Whether a date is today (year, month and day all match `new Date()`).
 * @param date - Date object
 */
export function isCurrentDay(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
}

/**
 * Extract the date portion from an ISO string (YYYY-MM-DDTHH:mm:ss -> YYYY-MM-DD)
 * @param isoString - ISO date string
 * @returns Date string in YYYY-MM-DD format
 */
export function extractDateFromISO(isoString: string): string {
    return isoString.split('T')[0];
}

/**
 * Format an ISO date string to DD/MM/YYYY display format
 * @param iso - ISO date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss), or null/undefined
 * @returns Formatted date string like "31/12/2025", or "—" if no date provided
 */
export function formatDateDDMMYYYY(iso?: string | null): string {
    if (!iso) return '—';
    const [y, m, d] = iso.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
}

/**
 * Get the Nth occurrence of a specific weekday in a month
 * @param year - Year
 * @param month - Month (1-12)
 * @param weekday - Day of week (0=Sunday, 1=Monday, etc.)
 * @param occurrence - Which occurrence (1=first, 2=second, etc., -1=last)
 * @returns Date object for the Nth weekday of the month
 */
export function getNthWeekdayOfMonth(year: number, month: number, weekday: number, occurrence: number): Date {
    if (occurrence === -1) {
        // Get last occurrence
        const lastDay = new Date(year, month, 0); // Last day of month
        const lastDayWeekday = lastDay.getDay();
        const diff = (lastDayWeekday - weekday + 7) % 7;
        lastDay.setDate(lastDay.getDate() - diff);
        return lastDay;
    } else {
        // Get Nth occurrence from start
        const firstDay = new Date(year, month - 1, 1);
        const firstDayWeekday = firstDay.getDay();
        const daysUntilTarget = (weekday - firstDayWeekday + 7) % 7;
        const targetDate = 1 + daysUntilTarget + (occurrence - 1) * 7;
        return new Date(year, month - 1, targetDate);
    }
}

/**
 * Get the 4th Monday of August for a given school year
 * School years start on August 1st
 * @param referenceDate - Optional reference date (defaults to today)
 * @returns Date object for the 4th Monday of August
 */
export function getSchoolYearStart(referenceDate: Date = new Date()): Date {
    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth() + 1; // 1-12

    // If we're before August, use previous year's August
    const augustYear = currentMonth < 8 ? currentYear - 1 : currentYear;

    // Get 4th Monday of August (weekday 1 = Monday)
    return getNthWeekdayOfMonth(augustYear, 8, 1, 4);
}

/**
 * Get the last Friday of June for a given school year
 * School years end at the end of June
 * @param referenceDate - Optional reference date (defaults to today)
 * @returns Date object for the last Friday of June
 */
export function getSchoolYearEnd(referenceDate: Date = new Date()): Date {
    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth() + 1; // 1-12

    // If we're August or later, use next year's June
    const juneYear = currentMonth >= 8 ? currentYear + 1 : currentYear;

    // Get last Friday of June (weekday 5 = Friday)
    return getNthWeekdayOfMonth(juneYear, 6, 5, -1);
}

